import React, { FC } from 'react';
import {
  Button,
  Dropdown,
  MultiSelectDropdown,
  Tooltip,
  useToasts,
} from 'freemium-ui';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useUpdateTopics } from 'routes/screens/response/hooks';
import { SentimentType } from 'routes/screens/response/types';
import Icon from 'shared/components/Icon';
import { SurveyResponseType } from 'shared/types';
import { SentimentEditContainer } from './styles';

const MAX_SENTIMENT_TAGS = 10;

type SentimentEditProps = {
  surveyResponse?: SurveyResponseType;
  setIsEdit: React.Dispatch<React.SetStateAction<boolean>>;
};

export type EditSentimentTagsFormType = {
  suggested_tags: {
    positive: string[] | null;
    neutral: string[] | null;
    negative: string[] | null;
  };
  customer_sentiment: string;
};

const SentimentTags: FC<{
  tags: string[];
  sentiment: SentimentType;
  onChange: (key: string, tags: string[]) => void;
}> = ({ tags, onChange, sentiment }) => {
  const { t } = useTranslation();
  return (
    <div className="topic-tags-container">
      <div className="container-heading">
        <span className={`dot-${sentiment}`}></span>
        {t(`responses.freddy_sentiment.${sentiment}`)}
      </div>
      <MultiSelectDropdown
        name={`${sentiment}-tags`}
        id={`modal-dd-tag-${sentiment}`}
        placeholder={
          tags.length > 0 ? '' : `${t('forms.placeholders.enter_topic_tags')}`
        }
        items={tags}
        selectedItem={tags.map((item) => ({
          id: item,
          topic: item,
        }))}
        disabled={tags.length >= MAX_SENTIMENT_TAGS}
        onChange={(t) => {
          onChange(
            `suggested_tags.${sentiment}`,
            t.map(({ topic }) => topic?.trim()).filter(Boolean),
          );
        }}
        customValues
        itemLabelIdentifier="topic"
        itemValueIdentifier="id"
      />
    </div>
  );
};

export const SentimentEdit: FC<SentimentEditProps> = ({
  surveyResponse,
  setIsEdit,
}) => {
  const { freddy_response_analysis, survey_id, surveyserv_response_id, id } =
    surveyResponse ?? {};
  const [, setToast] = useToasts();
  const { t } = useTranslation();

  const editSentimentTagsForm = useForm<EditSentimentTagsFormType>({
    defaultValues: {
      suggested_tags: {
        positive: freddy_response_analysis?.suggested_tags?.positive,
        neutral: freddy_response_analysis?.suggested_tags?.neutral,
        negative: freddy_response_analysis?.suggested_tags?.negative,
      },
      customer_sentiment: freddy_response_analysis?.customer_sentiment,
    },
  });

  const {
    setValue,
    watch,
    formState: { isDirty },
  } = editSentimentTagsForm;

  const translatedSentimentDropdownValues = (
    ['negative', 'neutral', 'positive'] as SentimentType[]
  ).map((sentiment) => {
    return {
      id: sentiment.toUpperCase(),
      label: t(`responses.freddy_sentiment.${t(sentiment)}`),
    };
  });

  const setFormValue = (fieldId: any, fieldValue: any) => {
    setValue(fieldId, fieldValue, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });
  };

  const positiveTags = watch('suggested_tags.positive');
  const negativeTags = watch('suggested_tags.negative');
  const neutralTags = watch('suggested_tags.neutral');
  const sentiment = watch('customer_sentiment');

  const { mutate: saveUpdatedTopics, isLoading: isLoadingTopics } =
    useUpdateTopics({
      setToast,
      callback: async (isSuccess: boolean) => {
        if (isSuccess) {
          setIsEdit(false);
        }
      },
    });

  const updateTopics = () => {
    saveUpdatedTopics({
      payload: {
        suggested_tags: {
          neutral: neutralTags,
          negative: negativeTags,
          positive: positiveTags,
        },
        customer_sentiment: sentiment,
      },
      surveyId: survey_id || '',
      surveyServResponseId: surveyserv_response_id || '',
      responseId: id || '',
    });
  };

  return (
    <SentimentEditContainer>
      <div className="edit-sentiment-text">
        {t('responses.freddy_sentiment.edit_sentiment')}
      </div>
      <div className="edit-sentiment-content">
        <div>
          <div className="dropdown-label">
            {t('responses.freddy_sentiment.overall_sentiment')}
            <Tooltip
              style={{ width: 400 }}
              label="Review and edit the sentiment if needed to improve predictions and update analytics"
              position="right"
            >
              <Icon width="12" height="12" glyph="info" fill="#465967" />
            </Tooltip>
          </div>
          <Dropdown
            id="modal-dd-dropdown"
            defaultSelectedItem={translatedSentimentDropdownValues.find(
              (item) => item.id === sentiment,
            )}
            emptyItemsText={`${t('no_results_found')}`}
            itemToString={(v: any) => v.label}
            itemComponent={(v: any) => v.label}
            items={translatedSentimentDropdownValues}
            onChange={(option) => {
              setFormValue('customer_sentiment', option.id);
            }}
          />
        </div>
        <div className="divider"></div>

        <div className="topics-text">
          {t('responses.freddy_sentiment.topics')}
        </div>
        <div className="tag-container-wrapper">
          <SentimentTags
            tags={positiveTags || []}
            sentiment="positive"
            onChange={setFormValue}
          />
          <SentimentTags
            tags={neutralTags || []}
            sentiment="neutral"
            onChange={setFormValue}
          />
          <SentimentTags
            tags={negativeTags || []}
            sentiment="negative"
            onChange={setFormValue}
          />
        </div>
      </div>
      <div className="edit-drawer-footer">
        <Button type="secondary" onClick={() => setIsEdit(false)}>
          {t('close')}
        </Button>
        <Button
          disabled={!isDirty || isLoadingTopics}
          onClick={() => updateTopics()}
        >
          {t('update')}
        </Button>
      </div>
    </SentimentEditContainer>
  );
};
